import Button from "components/Button/Button";
import Card from "components/Card/Card";
import ClaimNotesField from "components/ClaimNotesField";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import {
  Checkbox,
  DateOfBirthInput,
  MaskedInput,
  Select,
  STRING_TO_DATE,
  TextInput,
} from "components/inputs";
import LibertyMutualReferralFields from "components/LibertyMutualReferralFields";
import KaboodleModal from "components/Modals/KaboodleModal";
import Tooltip from "components/Tooltip/Tooltip";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import { GENDERS } from "constants/FormConstants";
import { format } from "date-fns";
import { isLibertyMutualFile } from "pages/future-medical/shared/future-medical-utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  useGetClaimantsQuery,
  usePatchClaimantByIdMutation,
} from "services/claimant-api";
import type { Section111ClaimantMatch } from "services/claims-api";
import {
  useGetFileByIdQuery,
  usePatchFileByIdMutation,
} from "services/file-api.ts";
import { useToken } from "utils/context";
import { useSelector } from "utils/redux";

import {
  formatClaimantLabel,
  formatOptionLabel,
  selectStyles,
} from "../../../utils/format-utils";
import ClaimNumberInput from "../ClaimNumberInput";

import PrefillClaimantFromSection111Modal from "./PrefillClaimantFromSection111Modal";

const SectionBreak = () => <div className="border grid-2-wide"></div>;

const activateOnEnterOrSpace =
  (onActivate: () => void): React.KeyboardEventHandler<HTMLSpanElement> =>
  (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate();
    }
  };

const ClaimantInformationForm = ({
  isReadOnly = false,
}: { isReadOnly?: boolean } = {}) => {
  const { fileId } = useParams<{ fileId: string }>();
  const constants = useSelector((state) => state.shared.constants);
  const { accessToken } = useToken();
  const [patchClaimantById] = usePatchClaimantByIdMutation();
  const [patchFileById] = usePatchFileByIdMutation();
  const [showClaimant, setShowClaimant] = useState(false);
  const [overridden, setOverridden] = useState(false);
  const [showOverrideConfirm, setShowOverrideConfirm] = useState(false);
  const [showPrefillModal, setShowPrefillModal] = useState(false);
  const prevSelectedClaimantIdRef = useRef<number | null>(null);

  const {
    control,
    reset,
    register,
    setError,
    clearErrors,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const { data: fileData, isLoading: isFileLoading } = useGetFileByIdQuery(
    { id: fileId },
    { skip: !fileId },
  );
  const claimantId = fileData?.claimants?.[0]?.id;

  // determine if claimant selection should be disabled (when file is marked completed)
  const isClaimantSelectionDisabled =
    isFileLoading || !fileData || fileData.status === "C";

  const isLibertyFeatureEnabled = Boolean(
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.LIBERTY_REFERRAL_FIELDS_ENABLED],
  );
  const isLiberty = useMemo(
    () => isLibertyFeatureEnabled && isLibertyMutualFile(fileData),
    [isLibertyFeatureEnabled, fileData],
  );

  // skip the claimants query if file is already completed (which means Claimant is disabled)
  const {
    data: claimants = { results: [], count: 0, next: null, previous: null },
  } = useGetClaimantsQuery({}, { skip: isClaimantSelectionDisabled });

  useEffect(() => {
    if (fileData) {
      if (
        !fileData.services?.some((service) => service.type === "111") ||
        (fileData?.claimants && fileData.claimants.length > 0)
      ) {
        setShowClaimant(true);
      }
      const selectedClaimantLabel =
        fileData.claimants.length === 0
          ? "Please select"
          : `${fileData?.claimants?.[0]?.first_name} ${fileData?.claimants?.[0]?.last_name}`;
      const selectedClaimantId = fileData?.claimants?.[0]?.id ?? null;
      const isSwitchingClaimants =
        prevSelectedClaimantIdRef.current !== selectedClaimantId;
      prevSelectedClaimantIdRef.current = selectedClaimantId;

      reset(
        {
          selected_claimant: {
            id: fileData?.claimants?.[0]?.id,
            label: selectedClaimantLabel,
          },
          dates_of_injury: fileData?.dates_of_injury || [],
          claim_numbers: fileData?.claim_numbers || [],
          first_name: fileData?.claimants?.[0]?.first_name || "",
          middle_name: fileData?.claimants?.[0]?.middle_name || "",
          last_name: fileData?.claimants?.[0]?.last_name || "",
          gender: fileData?.claimants?.[0]?.gender || "",
          ssn: fileData?.claimants?.[0]?.ssn || "",
          medicare_id: fileData?.claimants?.[0]?.medicare_id || "",
          address: fileData?.claimants?.[0]?.address || "",
          city: fileData?.claimants?.[0]?.city || "",
          state: fileData?.claimants?.[0]?.state || "",
          zip_code: fileData?.claimants?.[0]?.zip_code || "",
          is_medicare_eligible:
            fileData?.claimants?.[0]?.is_medicare_eligible || false,
          reasonable_expectation:
            fileData?.claimants?.[0]?.reasonable_expectation || false,
          is_represented: fileData?.is_represented || false,
          claimant_counsel: fileData?.claimant_counsel || "",
          attorney_name: fileData?.attorney_name || "",
          attorney_address: fileData?.attorney_address || "",
          attorney_phone: fileData?.attorney_phone || "",
          claimant_date_of_birth: fileData?.claimants?.[0]?.birth_date || "",
          grs_or_legacy_rcg: fileData?.grs_or_legacy_rcg || "",
          rcg_carrier: fileData?.rcg_carrier || "",
          segmentation_type: fileData?.segmentation_type || "",
          referral_type: fileData?.referral_type || "",
          wcrc_case_id: fileData?.wcrc_case_id || "",
          beneficiary_class: fileData?.beneficiary_class || "",
        },
        {
          // prevent currently edited values from being reset when incoming file data changes
          keepDirtyValues: !isSwitchingClaimants,
        },
      );
    }
  }, [fileData, reset]);

  // only prepare claimant options if we need them (file not completed)
  const claimantOptions = !isClaimantSelectionDisabled
    ? (claimants.results
        ?.map((c) => ({
          label: formatClaimantLabel(c),
          value: c.id,
          sortKey: c.last_name.toLowerCase(),
        }))
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey)) ?? [])
    : [];

  const handleSave = useCallback(
    async (
      api: "file" | "claimants" | "dates_of_injury",
      field: string,
      value: unknown,
    ) => {
      if (api === "file" && fileId) {
        await patchFileById({
          id: fileId,
          data: {
            [field]: value,
          },
        });
      }

      if (api === "claimants" && fileData?.claimants?.[0]?.id && accessToken) {
        try {
          await patchClaimantById({
            id: fileData?.claimants?.[0]?.id,
            data: {
              [field]: value,
            },
          }).unwrap();
          clearErrors(field);
        } catch (error) {
          setError(field, {
            type: error.status,
            message: error.data[field][0],
          });
        }
      }
    },
    [
      fileId,
      fileData,
      accessToken,
      patchFileById,
      patchClaimantById,
      clearErrors,
      setError,
    ],
  );

  const handlePrefillFromSection111 = useCallback(
    async (match: Section111ClaimantMatch) => {
      setShowPrefillModal(false);
      if (!claimantId) {
        KaboodleToaster.error(
          "Could not prefill: no claimant is selected on this file.",
        );
        return;
      }

      const fieldsToPrefill: Array<{
        formField: string;
        saveField?: string;
        value: string | null | undefined;
      }> = [
        { formField: "first_name", value: match.injured_party_first_name },
        { formField: "middle_name", value: match.injured_party_middle_initial },
        { formField: "last_name", value: match.injured_party_last_name },
        { formField: "gender", value: match.gender },
        { formField: "ssn", value: match.injured_party_ssn },
        { formField: "medicare_id", value: match.injured_party_hicn },
        {
          formField: "claimant_date_of_birth",
          saveField: "birth_date",
          value: match.injured_party_dob,
        },
      ];

      const toFill = fieldsToPrefill.filter(({ value }) => value);

      if (toFill.length === 0) {
        KaboodleToaster.info("That Section 111 record has no data to fill in.");
        return;
      }

      const patchData = Object.fromEntries(
        toFill.map(({ formField, saveField, value }) => [
          saveField ?? formField,
          value,
        ]),
      );

      try {
        await patchClaimantById({
          id: claimantId,
          data: patchData,
        }).unwrap();
        toFill.forEach(({ formField, value }) => {
          setValue(formField, value, { shouldDirty: true });
        });
        KaboodleToaster.success(
          `Overwrote ${toFill.length} field${toFill.length === 1 ? "" : "s"} with Section 111 data.`,
        );
      } catch (error) {
        KaboodleToaster.error(error);
      }
    },
    [claimantId, setValue, patchClaimantById],
  );

  const handleChangeClaimantOnFile = useCallback(
    async (o: OptionType) => {
      if (accessToken && fileId) {
        await patchFileById({
          id: fileId,
          data: {
            claimant_ids: [o.value?.toString()],
          },
        });
      }
    },
    [patchFileById, fileId, accessToken],
  );

  const handleOverride = useCallback(() => {
    setOverridden(true);
    setShowOverrideConfirm(false);
    KaboodleToaster.success("Claimant information fields can now be edited.");
  }, []);

  const handleLock = useCallback(() => {
    setOverridden(false);
    KaboodleToaster.success("Claimant information fields are now locked.");
  }, []);

  const claim_numbers = watch("claim_numbers");

  if (!showClaimant) return null;

  return (
    <div>
      <PrefillClaimantFromSection111Modal
        isOpen={showPrefillModal}
        onClose={() => setShowPrefillModal(false)}
        claimantId={claimantId}
        onSelect={handlePrefillFromSection111}
      />

      {showOverrideConfirm && (
        <KaboodleModal
          isOpen={showOverrideConfirm}
          onClose={() => setShowOverrideConfirm(false)}
          footer={
            <div className="flex jc-c">
              <Button className="delete" onClick={handleOverride}>
                Unlock Fields
              </Button>
              <Button onClick={() => setShowOverrideConfirm(false)}>
                Cancel
              </Button>
            </div>
          }
        >
          <Card heading="Unlock Claimant Information">
            <p>
              Are you sure you want to unlock editing for claimant selection,
              dates of injury, and claim numbers? This will allow you to modify
              these critical file details and could impact any open workspaces
              associated with the file.
            </p>
          </Card>
        </KaboodleModal>
      )}

      <Card
        heading="Claimant Information"
        cardNumber={1}
        headerButton={
          <div className="flex jc-end ai-c" style={{ gap: "8px" }}>
            {!isReadOnly && (
              <Tooltip
                content="Prefill from Section 111"
                place="left"
                variant="info"
              >
                <span
                  className="material-icons lock-button"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (claimantId) setShowPrefillModal(true);
                  }}
                  onKeyDown={activateOnEnterOrSpace(() => {
                    if (claimantId) setShowPrefillModal(true);
                  })}
                  style={{
                    cursor: claimantId ? "pointer" : "not-allowed",
                    fontSize: "20px",
                    opacity: claimantId ? 1 : 0.4,
                  }}
                >
                  auto_fix_high
                </span>
              </Tooltip>
            )}
            <span
              className="material-icons lock-button"
              role="button"
              tabIndex={0}
              onClick={
                overridden ? handleLock : () => setShowOverrideConfirm(true)
              }
              onKeyDown={activateOnEnterOrSpace(
                overridden ? handleLock : () => setShowOverrideConfirm(true),
              )}
              style={{ cursor: "pointer", fontSize: "20px" }}
            >
              {overridden ? "lock_open" : "lock"}
            </span>
          </div>
        }
      >
        <Controller
          control={control}
          name="selected_claimant"
          render={({ field: { name, value } }) => (
            <Select
              label="Claimant"
              name={name}
              errors={errors}
              options={claimantOptions}
              value={value}
              onChange={(o: OptionType) => handleChangeClaimantOnFile(o)}
              required={true}
              styles={selectStyles}
              formatOptionLabel={formatOptionLabel}
              readOnly={
                isReadOnly || isClaimantSelectionDisabled || !overridden
              }
            />
          )}
        />

        <ClaimNumberInput
          initialClaimNumbers={claim_numbers}
          isReadOnly={isReadOnly || fileData?.status === "C" || !overridden}
        />
        <SectionBreak />

        <Controller
          name="first_name"
          control={control}
          render={({ field }) => {
            return (
              <TextInput
                {...field}
                label="First Name"
                errors={errors}
                readOnly={isReadOnly}
                onBlur={() =>
                  handleSave("claimants", field.name, getValues(field.name))
                }
              />
            );
          }}
        />
        <Controller
          name="middle_name"
          control={control}
          render={({ field }) => {
            return (
              <TextInput
                {...field}
                label="Middle Name"
                errors={errors}
                readOnly={isReadOnly}
                onBlur={() =>
                  handleSave("claimants", field.name, getValues(field.name))
                }
              />
            );
          }}
        />
        <Controller
          name="last_name"
          control={control}
          render={({ field }) => {
            return (
              <TextInput
                {...field}
                label="Last Name"
                errors={errors}
                readOnly={isReadOnly}
                onBlur={() =>
                  handleSave("claimants", field.name, getValues(field.name))
                }
              />
            );
          }}
        />
        <Controller
          name="gender"
          control={control}
          render={({ field: { name, onChange, value } }) => {
            return (
              <Select
                name={name}
                onChange={(option: OptionType | null) =>
                  onChange(option?.value ?? null)
                }
                label="Gender"
                errors={errors}
                options={GENDERS}
                value={GENDERS?.find(
                  (option: OptionType) => option.value === value,
                )}
                readOnly={isReadOnly}
                onBlur={() => handleSave("claimants", name, getValues(name))}
              />
            );
          }}
        />
        <Controller
          name="claimant_date_of_birth"
          control={control}
          render={({ field: { name, value } }) => {
            return (
              <DateOfBirthInput
                key={value}
                name={name}
                initialDate={STRING_TO_DATE(value)}
                allowFutureDates={true}
                readOnly={isReadOnly}
                onDateChange={(date) =>
                  handleSave(
                    "claimants",
                    "birth_date",
                    format(date, "yyyy-MM-dd"),
                  )
                }
                errors={errors}
              />
            );
          }}
        />
        <SectionBreak />
        <Controller
          name="address"
          control={control}
          render={({ field }) => {
            return (
              <TextInput
                {...field}
                label="Address"
                errors={errors}
                readOnly={isReadOnly}
                onBlur={() =>
                  handleSave("claimants", field.name, getValues(field.name))
                }
              />
            );
          }}
        />
        <Controller
          name="city"
          control={control}
          render={({ field }) => {
            return (
              <TextInput
                {...field}
                label="City"
                errors={errors}
                readOnly={isReadOnly}
                onBlur={() =>
                  handleSave("claimants", field.name, getValues(field.name))
                }
              />
            );
          }}
        />
        <Controller
          name="state"
          control={control}
          render={({ field: { name, onChange, value } }) => {
            return (
              <Select
                name={name}
                onChange={onChange}
                label="State"
                errors={errors}
                options={constants?.STATES}
                value={constants?.STATES?.find(
                  (option: OptionType) => option.value === value,
                )}
                readOnly={isReadOnly}
                onBlur={() => {
                  const option = getValues(name);
                  handleSave("claimants", name, option.value);
                }}
              />
            );
          }}
        />
        <Controller
          name="zip_code"
          control={control}
          render={({ field }) => {
            return (
              <MaskedInput
                {...field}
                mask="zipcode"
                label="Zipcode"
                errors={errors}
                readOnly={isReadOnly}
                onBlur={() =>
                  handleSave("claimants", field.name, getValues(field.name))
                }
              />
            );
          }}
        />
        <Controller
          name="ssn"
          control={control}
          render={({ field }) => {
            return (
              <MaskedInput
                {...field}
                mask="ssn"
                label="SSN"
                errors={errors}
                readOnly={isReadOnly}
                onBlur={() =>
                  handleSave("claimants", field.name, getValues(field.name))
                }
              />
            );
          }}
        />
        <SectionBreak />
        <Controller
          name="medicare_id"
          control={control}
          render={({ field }) => {
            return (
              <MaskedInput
                {...field}
                mask="medicare"
                label="Medicare ID"
                errors={errors}
                readOnly={isReadOnly}
                onBlur={() =>
                  handleSave("claimants", field.name, getValues(field.name))
                }
              />
            );
          }}
        />
        <Controller
          name="is_medicare_eligible"
          control={control}
          render={({ field: { value, onChange, ...field } }) => {
            return (
              <Checkbox
                {...field}
                checked={value}
                readOnly={isReadOnly}
                onChange={(e) => {
                  onChange(e.target.checked);
                  handleSave(
                    "claimants",
                    "is_medicare_eligible",
                    e.target.checked,
                  );
                }}
                label="Medicare Eligible"
              />
            );
          }}
        />
        <Controller
          name="reasonable_expectation"
          control={control}
          render={({ field }) => {
            return (
              <Checkbox
                {...field}
                checked={field.value}
                readOnly={isReadOnly}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                  handleSave(
                    "claimants",
                    "reasonable_expectation",
                    e.target.checked,
                  );
                }}
                label="Reasonable Expectation"
              />
            );
          }}
        />
        <Checkbox
          {...register("is_represented", {
            onChange: () => {
              handleSave("file", "is_represented", getValues("is_represented"));
            },
          })}
          readOnly={isReadOnly}
          label="Is Represented"
        />
        <Controller
          name="claimant_counsel"
          control={control}
          render={({ field }) => {
            return (
              <TextInput
                {...field}
                label="P/C Firm Name"
                errors={errors}
                readOnly={isReadOnly}
                onBlur={() =>
                  handleSave("file", field.name, getValues(field.name))
                }
              />
            );
          }}
        />
        <Controller
          name="attorney_name"
          control={control}
          render={({ field }) => {
            return (
              <TextInput
                {...field}
                label="Attorney Name"
                errors={errors}
                readOnly={isReadOnly}
                onBlur={() =>
                  handleSave("file", field.name, getValues(field.name))
                }
              />
            );
          }}
        />
        <Controller
          name="attorney_address"
          control={control}
          render={({ field }) => {
            return (
              <TextInput
                {...field}
                label="Attorney Address"
                errors={errors}
                readOnly={isReadOnly}
                onBlur={() =>
                  handleSave("file", field.name, getValues(field.name))
                }
              />
            );
          }}
        />
        <Controller
          name="attorney_phone"
          control={control}
          render={({ field }) => {
            return (
              <TextInput
                {...field}
                label="Attorney Phone"
                errors={errors}
                readOnly={isReadOnly}
                onBlur={() =>
                  handleSave("file", field.name, getValues(field.name))
                }
              />
            );
          }}
        />
      </Card>

      {/* Never mounted for non-Liberty files, so there's no rcg_carrier
          value left behind to clean up here -- the useController/useEffect
          inside LibertyMutualReferralFields that clears rcg_carrier on
          leaving RCG simply never registers, and reset() above already
          defaults these 5 fields to "" regardless. */}
      {isLiberty && (
        <>
          {/* cardNumber={2} is hardcoded, sequential with the sibling
              Claimant Information card's cardNumber={1} above -- hardcoding
              a cardNumber is the existing pattern in this file, though this
              particular card and its 2 are new to this branch.
              required intentionally left unset, same as the FMOperations
              and AllocationReview call sites (see LibertyMutualReferralFields's
              own comment on its required prop) -- FileSerializer keeps
              these fields optional here, unlike intake. */}
          <Card heading="Liberty Mutual Referral Information" cardNumber={2}>
            <LibertyMutualReferralFields
              control={control}
              errors={errors}
              readOnly={isReadOnly}
              showBeneficiaryClass
              // handleSave's value param is typed unknown (shared across the
              // "file"/"claimants"/"dates_of_injury" saves above, which take
              // different value shapes) -- a superset of the string this
              // expects, intentionally loose the same way
              // LibertyMutualReferralFields's own control/errors props
              // already are (see its own top-of-file comment), not a real
              // mismatch.
              onFieldSave={(field, value) => handleSave("file", field, value)}
            />
          </Card>
          <ClaimNotesField
            fileId={fileId}
            readOnly={isReadOnly}
            variant="operations"
            // Matches the sibling "Claimant Information" card's cardNumber
            // above -- purely cosmetic (feeds an otherwise-unused card-N CSS
            // class, see Card.tsx), but keeps both cards under this section
            // consistent instead of leaving this one on ClaimNotesField's
            // unrelated default.
            cardNumber={1}
          />
        </>
      )}
    </div>
  );
};

export default ClaimantInformationForm;
